import React, { useEffect, useState } from 'react';
import {
    Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
    Stack, Tab, Tabs, Typography, useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import EastIcon from '@mui/icons-material/East';
import SouthIcon from '@mui/icons-material/South';
import { BRAND, LIBRARY_DISCLAIMER } from './libraryConstants';
import { STATUS } from './StatusChip';

const ROLE_LABELS = { viewer: 'Committee member', contributor: 'Contributor', approver: 'Approver' };

const Steps = ({ items }) => (
    <Box component="ol" sx={{ pl: 2.5, my: 1, '& li': { mb: 1.25, lineHeight: 1.55 } }}>
        {items.map((text, i) => <li key={i}><Typography variant="body2" component="span">{text}</Typography></li>)}
    </Box>
);

const Heading = ({ children }) => (
    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: BRAND.primary, mt: 2.5, mb: 0.5 }}>{children}</Typography>
);

const StatusPill = ({ status }) => {
    const s = STATUS[status];
    return <Chip size="small" label={s.label} color={s.color} variant={s.variant} />;
};

// Draft -> In review -> Published -> Superseded, with the two side exits.
// Horizontal on wide screens, vertical on phones.
const StatusFlow = () => {
    const theme = useTheme();
    const narrow = useMediaQuery(theme.breakpoints.down('sm'));
    const Arrow = narrow ? SouthIcon : EastIcon;
    const step = (status, action) => (
        <React.Fragment key={status}>
            {action && (
                <Stack direction={narrow ? 'row' : 'column'} alignItems="center" spacing={0.5} sx={{ color: 'text.secondary', px: 0.5 }}>
                    <Arrow fontSize="small" />
                    <Typography variant="caption" sx={{ textAlign: 'center', maxWidth: 110, lineHeight: 1.2 }}>{action}</Typography>
                </Stack>
            )}
            <StatusPill status={status} />
        </React.Fragment>
    );
    return (
        <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#F5F8FC', border: '1px solid', borderColor: 'divider' }}>
            <Stack direction={narrow ? 'column' : 'row'} alignItems="center" justifyContent="center" spacing={1} sx={{ flexWrap: 'wrap' }}>
                {step('draft')}
                {step('in_review', 'Submit for review')}
                {step('published', 'Approve & publish')}
                {step('superseded', 'Newer revision published')}
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.5, sm: 3 }} justifyContent="center" sx={{ mt: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                    <strong>In review → Draft:</strong> an approver returns it with a note
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    <strong>Published → Withdrawn:</strong> an approver removes it, with a reason
                </Typography>
            </Stack>
        </Box>
    );
};

const StatusGlossary = () => (
    <Stack spacing={1} sx={{ mt: 1 }}>
        {Object.keys(STATUS).map(k => (
            <Stack key={k} direction="row" spacing={1.5} alignItems="flex-start">
                <Box sx={{ minWidth: 96 }}><StatusPill status={k} /></Box>
                <Typography variant="body2" color="text.secondary">{STATUS[k].help}</Typography>
            </Stack>
        ))}
    </Stack>
);

const FindSection = () => (
    <>
        <Typography variant="body2">
            Everyone on the committee can search the library and download published devices.
        </Typography>
        <Heading>Find a device</Heading>
        <Steps items={[
            'Tick a device type or manufacturer to narrow the list. The number beside each option is how many published devices it would show.',
            'Type in the search box to match a library code, model, ordering code or description. Every word you type must match, so "351 SEL" finds devices with both.',
            'Click a row to open its details: the preview, files, revisions and verification status.',
            'The address bar keeps your filters and the open device, so you can copy the link to share a view.'
        ]} />
        <Heading>Download</Heading>
        <Steps items={[
            'Tick one or more rows, then click Download footprints. Use the arrow beside it for other file types or for everything.',
            'If your browser asks to allow multiple downloads, allow it. A list of links also appears; they work for 15 minutes.',
            'Files are named after the library code, for example PR_SEL_351S7.dwg. The revision is shown on the page, not in the file name, so block names stay the same between revisions.',
            'To download an older revision, open the device and pick it under Revisions.'
        ]} />
        <Heading>Codes and badges</Heading>
        <Typography variant="body2">
            A library code is DEVICE TYPE_MANUFACTURER_MODEL, plus a variant when there is one: <strong>PR_SEL_351S7</strong> is a
            protective relay (PR) from SEL, model 351S7. The verification badge says how far the content has been checked:
            Unverified, Committee reviewed or Manufacturer verified.
        </Typography>
        <Alert severity="info" sx={{ mt: 2 }}>{LIBRARY_DISCLAIMER}</Alert>
    </>
);

const ContributeSection = ({ role }) => (
    <>
        {role === 'viewer' && (
            <Alert severity="info" sx={{ mb: 2 }}>
                You can browse and download. To add devices, ask your committee chair to make you a contributor.
            </Alert>
        )}
        <Typography variant="body2">
            Contributors are chosen by the committee chair. Chairs, co-chairs and approvers can contribute too.
        </Typography>
        <Heading>How a device moves through the library</Heading>
        <StatusFlow />
        <Heading>Add a device</Heading>
        <Steps items={[
            'Click Add a device. Choose the device type and manufacturer, then enter the model. Add a variant only when it differs from the base model (for example WITHOUT-PUSHBUTTONS).',
            'Watch the library code build at the top of the form as you type. That is the name members will download.',
            'Click Save draft. The file slots unlock once the draft exists.',
            'Upload the Footprint DWG (required). A preview image is strongly recommended because it shows in search results. A footprint PDF, schematic DWG and other files are optional. Each file can be up to 50 MB.',
            'Uploading another footprint or preview replaces the old one. While it is a draft you can remove files with the bin icon.',
            'Click Submit for review. An approver checks it before anyone else can see it.'
        ]} />
        <Heading>After you submit</Heading>
        <Steps items={[
            'My drafts lists everything of yours that is not published yet, with its status.',
            'If an approver returns it, open it from My drafts: their note is shown at the top. Fix it, then submit again.',
            'Once approved, it appears in Browse for the whole committee.'
        ]} />
        <Heading>Update a published device</Heading>
        <Steps items={[
            'Open the device and click New revision. If you try to add a code that already exists, the form offers this too.',
            'A revision keeps the same device type, manufacturer, model and variant. Files are not copied, so upload the updated ones.',
            'When the new revision is approved, the old one becomes Superseded. It stays downloadable from the Revisions list.'
        ]} />
    </>
);

const ReviewSection = ({ role }) => (
    <>
        {role !== 'approver' && (
            <Alert severity="info" sx={{ mb: 2 }}>
                This part is for approvers: the committee chair and co-chair, admins, and anyone the chair adds as an approver.
            </Alert>
        )}
        <Heading>Review and publish</Heading>
        <Steps items={[
            'Review queue shows everything waiting, oldest first. The number on the tab is how many are waiting.',
            'Open View details to check the files and information. You can Edit to correct details before publishing.',
            'Approve & publish makes it visible to the committee. Tick "Email committee members" to announce it.',
            'Return to draft sends it back to the contributor. A note is required so they know what to fix.',
            'You cannot approve something you created or submitted yourself. Another approver has to do it; the button explains this when it is greyed out.'
        ]} />
        <Heading>Withdraw</Heading>
        <Typography variant="body2">
            Open a published device and click Withdraw, giving a reason. It disappears from search and its files can no longer
            be downloaded. Anyone following an old link sees that it was withdrawn, and why.
        </Typography>
        <Heading>Roles (chair, co-chair and admins)</Heading>
        <Typography variant="body2">
            The Roles tab picks who can contribute and who can approve, from the committee's members. The chair and co-chair can always approve.
        </Typography>
        <Heading>Statuses</Heading>
        <StatusGlossary />
    </>
);

const SECTIONS = [
    { value: 'find', label: 'Find & download' },
    { value: 'contribute', label: 'Add a device' },
    { value: 'review', label: 'Review & publish' }
];
const DEFAULT_SECTION = { viewer: 'find', contributor: 'contribute', approver: 'review' };

const LibraryHelp = ({ open, onClose, role = 'viewer', initialSection }) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [section, setSection] = useState(initialSection || DEFAULT_SECTION[role]);

    useEffect(() => {
        if (open) setSection(initialSection || DEFAULT_SECTION[role]);
    }, [open, role, initialSection]);

    return (
        <Dialog open={open} onClose={onClose} fullScreen={fullScreen} fullWidth maxWidth="md" aria-labelledby="library-help-title">
            <DialogTitle id="library-help-title" sx={{ pr: 6 }}>
                How the Device Library works
                <Typography variant="body2" color="text.secondary">Your role: {ROLE_LABELS[role] || role}</Typography>
                <IconButton onClick={onClose} aria-label="Close help" sx={{ position: 'absolute', right: 12, top: 12 }}><CloseIcon /></IconButton>
            </DialogTitle>
            <Tabs value={section} onChange={(e, v) => setSection(v)} variant="scrollable" allowScrollButtonsMobile
                sx={{ px: 2, borderBottom: 1, borderColor: 'divider', '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}>
                {SECTIONS.map(s => <Tab key={s.value} value={s.value} label={s.label} />)}
            </Tabs>
            <DialogContent sx={{ pt: 2 }}>
                {section === 'find' && <FindSection />}
                {section === 'contribute' && <ContributeSection role={role} />}
                {section === 'review' && <ReviewSection role={role} />}
            </DialogContent>
            <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
        </Dialog>
    );
};

export default LibraryHelp;
